/**
 * SEO Head Manager - Centralized SEO meta tag management for PosterFire.com
 * Manages: canonical, robots, title, description, OG tags, Twitter cards, JSON-LD
 */
const CANONICAL_BASE = 'https://posterfire.com';

export const SEO = {
  CANONICAL_BASE,

  /**
   * Set or create a meta tag
   */
  setMeta(selector, attr, val, createTag, createAttr) {
    let el = document.querySelector(selector);
    if (!el && createTag) {
      el = document.createElement(createTag);
      if (createAttr) Object.entries(createAttr).forEach(([k, v]) => el.setAttribute(k, v));
      document.head.appendChild(el);
    }
    if (el) el.setAttribute(attr, val);
    return el;
  },

  /**
   * Set the canonical URL
   */
  setCanonical(url) {
    let el = document.querySelector('link[rel="canonical"]');
    if (!el) {
      el = document.createElement('link');
      el.setAttribute('rel', 'canonical');
      document.head.appendChild(el);
    }
    el.setAttribute('href', url);
  },

  /**
   * Set robots directive (index,follow OR noindex,nofollow)
   */
  setRobots(directive) {
    this.setMeta('meta[name="robots"]', 'content', directive);
  },

  /**
   * Apply full SEO tags for a product page
   */
  applyProductSEO(product, canonicalUrl) {
    document.title = this.buildProductTitle(product);

    const desc = this.buildProductDescription(product);
    const imgUrl = this.getFullImageUrl(product.image_url);

    this.setCanonical(canonicalUrl);
    this.setRobots('index, follow');
    this.setMeta('meta[name="description"]', 'content', desc);

    // Open Graph
    this.setMeta('meta[property="og:title"]', 'content', document.title, 'meta', { property: 'og:title' });
    this.setMeta('meta[property="og:description"]', 'content', desc, 'meta', { property: 'og:description' });
    this.setMeta('meta[property="og:image"]', 'content', imgUrl, 'meta', { property: 'og:image' });
    this.setMeta('meta[property="og:url"]', 'content', canonicalUrl, 'meta', { property: 'og:url' });
    this.setMeta('meta[property="og:type"]', 'content', 'product', 'meta', { property: 'og:type' });

    // Twitter
    this.setMeta('meta[name="twitter:card"]', 'content', 'summary_large_image', 'meta', { name: 'twitter:card' });
    this.setMeta('meta[name="twitter:title"]', 'content', document.title, 'meta', { name: 'twitter:title' });
    this.setMeta('meta[name="twitter:description"]', 'content', desc, 'meta', { name: 'twitter:description' });
    this.setMeta('meta[name="twitter:image"]', 'content', imgUrl, 'meta', { name: 'twitter:image' });
  },

  /**
   * Apply SEO for a category/collection page
   */
  applyCategorySEO(catObj, categoryId) {
    const catName = catObj?.name || categoryId || 'All';
    const title = catObj?.seo_title || `${catName} Posters | PosterFire`;
    const desc = catObj?.seo_description || `Shop premium ${catName.toLowerCase()} posters and wall art. High-quality prints with fast shipping at PosterFire.com.`;
    const url = categoryId ? `${CANONICAL_BASE}/shop?category=${categoryId}` : `${CANONICAL_BASE}/shop`;

    document.title = title;
    this.setCanonical(url);
    this.setRobots('index, follow');
    this.setMeta('meta[name="description"]', 'content', desc);
    if (catObj?.seo_keywords) {
      this.setMeta('meta[name="keywords"]', 'content', catObj.seo_keywords, 'meta', { name: 'keywords' });
    }

    this.setMeta('meta[property="og:title"]', 'content', title, 'meta', { property: 'og:title' });
    this.setMeta('meta[property="og:description"]', 'content', desc, 'meta', { property: 'og:description' });
    this.setMeta('meta[property="og:url"]', 'content', url, 'meta', { property: 'og:url' });
    this.setMeta('meta[property="og:type"]', 'content', 'website', 'meta', { property: 'og:type' });
  },

  /**
   * Apply noindex for low-value pages (search, cart, checkout, etc.)
   */
  applyNoIndex(title, path) {
    document.title = title;
    this.setRobots('noindex, nofollow');
    this.setCanonical(`${CANONICAL_BASE}${path || ''}`);
    // Remove OG URL so Google doesn't discover these via social
    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) ogUrl.remove();
  },

  /**
   * Reset to defaults (used on cleanup)
   */
  resetDefaults() {
    document.title = 'PosterFire — Premium Posters, Wall Art & Home Decor';
    this.setCanonical(CANONICAL_BASE);
    this.setRobots('index, follow');
  },

  buildProductTitle(product) {
    let title = product.title;
    if (title.length > 50) title = title.substring(0, 50);
    return `${title} | PosterFire.com`;
  },

  buildProductDescription(product) {
    const name = product.title;
    const cat = product.category || '';
    const sizes = product.sizes?.length
      ? `Available in ${product.sizes.map(s => s.size).join(', ')} sizes. `
      : '';
    let desc = `Buy ${name} ${cat} poster. ${sizes}Free shipping available.`;
    if (desc.length > 160) desc = desc.substring(0, 157) + '...';
    return desc;
  },

  getFullImageUrl(url) {
    if (!url) return '';
    if (url.startsWith('/api/uploads/')) return `${CANONICAL_BASE}${url}`;
    if (url.startsWith('http')) return url;
    return `${CANONICAL_BASE}${url}`;
  },

  buildAltText(product) {
    const parts = [product.title];
    if (product.category) parts.push(product.category);
    parts.push('poster');
    if (product.sizes?.length) {
      const defaultSize = product.sizes[1] || product.sizes[0];
      if (defaultSize) parts.push(`${defaultSize.size} inches`);
    }
    return parts.join(' ');
  }
};

export default SEO;
